import {Inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {ApiClientConfig} from '../../api-client-config';
import {
  AppAssessmentDetails,
  AppAssessmentDocCreateRequest,
  AppAssessmentDocUpdateRequest,
  AppAssessmentOverrideOnDuplicate,
  AppAssessmentSimplifiedCreateRequest,
  AppAssessmentSimplifiedUpdateRequest,
  CommonDocumentEditorDetails,
  DocRenameRequest,
  DocumentDuplicateResponse,
  DocumentLockResponse,
  IsValidableResponse,
} from '@primatoo-ms-organization';
import {map} from 'rxjs/operators';
import {BusinessProjectManagementService} from '../../interface/business-project-management-service';
import {
  GetAppAsPermissionResponse
} from "it/primatoo/openapi/api/org/schemas/appassessmentdoc/GetAppAsPermissionResponse";
import {
  UpdateAppAsPermissionRequest
} from "it/primatoo/openapi/api/org/schemas/appassessmentdoc/UpdateAppAsPermissionRequest";
import {
  GetAppAsAvailableOperationsResponse
} from "it/primatoo/openapi/api/org/schemas/appassessmentdoc/GetAppAsAvailableOperationsResponse";
import {
  ListAppAssessmentForAppRefResponse
} from "it/primatoo/openapi/api/org/schemas/appassessment/ListAppAssessmentForAppRefResponse";
import {ColumnWithLocalization} from "it/primatoo/openapi/api/org/schemas/common/ColumnWithLocalization";
import {GetChapterResponse} from "it/primatoo/openapi/api/org/schemas/common/docs/GetChapterResponse";
import {ListTemplateChapterResponse} from "it/primatoo/openapi/api/org/schemas/common/docs/ListTemplateChapterResponse";
import {DocResourceStatus} from "it/primatoo/openapi/api/org/schemas/common/DocResourceStatus";
import {ListDocumentResponse} from "it/primatoo/openapi/api/org/schemas/common/docs/ListDocumentResponse";
import {CreateDocResponse} from "it/primatoo/openapi/api/org/schemas/common/docs/CreateDocResponse";
import {DocumentItem} from "it/primatoo/openapi/api/org/schemas/common/docs/_listdocumentresponse/DocumentItem";
import {UpdateDocResponse} from "it/primatoo/openapi/api/org/schemas/common/docs/UpdateDocResponse";
import {
  ListAppForNewAssessmentResponse
} from "it/primatoo/openapi/api/org/schemas/appassessment/ListAppForNewAssessmentResponse";
import { UpsertWidgetsRequest } from 'it/primatoo/openapi/api/org/schemas/common/docs/UpsertWidgetsRequest';

@Injectable({
  providedIn: 'root'
})
export class AppAssessmentsService extends BusinessProjectManagementService {
  private readonly endpointV1: string;
  private readonly endpointV2: string;
  private readonly docsEndpoint: string;
  private readonly docsV2Endpoint: string;

  constructor(
    @Inject(ApiClientConfig) private config,
    public httpClient: HttpClient) {
    super(httpClient);
    this.endpointV1 = this.config.endPoint + '/api/org/app-assessments/';
    this.endpointV2 = this.config.endPoint + '/api/org/v2/app-assessments/';
    this.docsEndpoint = this.config.endPoint + '/api/org/docs/app-assessments/';
    this.docsV2Endpoint = this.config.endPoint + '/api/org/v2/docs/app-assessments/';

  }

  getEndPointV1(): string {
    return this.endpointV1;
  }

  getEndPointV2(): string {
    return this.endpointV2;
  }

  // ----------------------------------- API DOC V2 ----------------------------------- //
  getActivePermission(docId: number): Observable<GetAppAsPermissionResponse> {
    const url = this.docsV2Endpoint + `${docId}/permissions/`;
    return this.httpClient.get<GetAppAsPermissionResponse>(url);
  }

  patchActivePermission(docId: number, request: UpdateAppAsPermissionRequest): Observable<void> {
    const url = this.docsV2Endpoint + `${docId}/permissions/`;
    return this.httpClient.patch<void>(url, request);
  }


  // ----------------------------------- API V2 ----------------------------------- //

  getTreatmentTemplateChapterId(id: number, chapterId: number): Observable<GetChapterResponse> {
    return this.httpClient.get<GetChapterResponse>(this.endpointV2 + `${id}/latest/template/chapters/${chapterId}/`);
  }

  getDocumentTemplateChapters(id: number): Observable<ListTemplateChapterResponse> {
    return this.httpClient.get<ListTemplateChapterResponse>(this.endpointV2 + `${id}/latest/template/chapters/`);
  }

  // findAll(): Observable<Array<Document>> {
  //   const url = this.endpointV2 + 'latest/';
  //   return this.httpClient.get<ListDocumentResponse>(url).pipe(map(a => a.documents));
  // }

  findAllFiltered(filter?: string, filterByAnyPermission?: boolean,
                  columns?: string[]): Observable<Array<DocumentItem>> {
    let url = this.endpointV2 + `latest/`;
    url += (filter ? ('?' + filter) : '');
    url += filter ? '&sort=docId,DESC' : '?sort=docId,DESC';
    url += (filterByAnyPermission ? '&filterByAnyPermission=true' : '&filterByAnyPermission=false');
    if (columns && columns.length > 0) {
      url += '&columns='+columns.join('%7C');
    }
    return this.httpClient.get<ListDocumentResponse>(url).pipe(
      map(response => {
        return response.documents;
      }));
  }

  // ----------------------------------- API V1 ----------------------------------- //
  getDocColumn(): Observable<ColumnWithLocalization[]> {
    const url = this.docsEndpoint + `columns/`;
    return this.httpClient.get<ColumnWithLocalization[]>(url);
  }

  create(body: AppAssessmentDocCreateRequest): Observable<CreateDocResponse> {
    const url = this.docsEndpoint
    return this.httpClient.post<CreateDocResponse>(url, body);
  }

  delete(id: number): Observable<void> {
    const url = this.docsEndpoint + `${id}/`;
    return this.httpClient.delete<void>(url);
  }


  update(id: number, body: AppAssessmentDocUpdateRequest): Observable<UpdateDocResponse> {
    const url = this.docsEndpoint + `${id}/`;
    return this.httpClient.put<UpdateDocResponse>(url, body);
  }

  rename(id: number, request: DocRenameRequest): Observable<void> {
    const url = this.docsEndpoint + `${id}/rename`;
    return this.httpClient.patch<void>(url, request);
  }

  getToOverride(): Observable<AppAssessmentOverrideOnDuplicate> {
    const url = this.endpointV1 + `override-on-duplicate`;
    return this.httpClient.get<AppAssessmentOverrideOnDuplicate>(url);
  }

  duplicate(id: number, request: AppAssessmentDocCreateRequest): Observable<DocumentDuplicateResponse> {
    const url = this.endpointV1 + `${id}/latest/duplicate`;
    return this.httpClient.post<DocumentDuplicateResponse>(url, request);
  }

  reopen(id: number): Observable<void> {
    const url = this.endpointV1 + `${id}/latest/re-open`;
    return this.httpClient.post<void>(url, {});
  }

  getDetails(id: number): Observable<AppAssessmentDetails> {
    const url = this.endpointV1 + `${id}/latest/details`;
    return this.httpClient.get<AppAssessmentDetails>(url);
  }

  getEditorDetails(id: number): Observable<CommonDocumentEditorDetails> {
    return this.getDetails(id).pipe(map(m => {
      const response: CommonDocumentEditorDetails = {
        docId: m.docId,
        status: m.status as DocResourceStatus,
        statusLabel: m.statusLabel,
        name: m.name,
        userCheck: m.userCheck,
        description: m.description,
        warnings: [],
        internalId: m.internalId,
        parentId: m.parentId,
        parentName: m.parentName,
        completed: m.completed,
        creatorUserId: m.creatorUserId,
        updatedAt: m.updatedAt,

        applicationId: m.applicationId,
        applicationName: m.applicationName,

        category: m.category
      }
      return response;
    }));

    // const url = this.endpointV2 + `${id}/latest/editor-details/`;
    // return this.httpClient.get<GetTreatmentForEditorDetailResponse>(url);
  }

  upsertWidgets(id: number, widgets: UpsertWidgetsRequest): Observable<void> {
    const url = this.endpointV2 + `${id}/latest/widgets/`;
    return this.httpClient.post<void>(url, widgets);
  }

  releaseLock(id: number): Observable<void> {
    const url = this.endpointV1 + `${id}/latest/lock`;
    return this.httpClient.delete<void>(url, {});
  }

  lockResource(id: number, token: number): Observable<DocumentLockResponse> {
    let url = this.endpointV1 + `${id}/latest/lock`;
    if (token !== null) {
      url = `${url}?token=${token}`;
    }
    return this.httpClient.post<DocumentLockResponse>(url, {});
  }

  publish(id: number): Observable<void> {
    const url = this.endpointV1 + `${id}/latest/publish`;
    return this.httpClient.post<void>(url, {});
  }

  isValidable(id: number): Observable<IsValidableResponse> {
    const url = this.endpointV1 + `${id}/latest/is-validable`;
    return this.httpClient.get<IsValidableResponse>(url);
  }

  refreshResults(id: number): Observable<void> {
    const url = this.endpointV1 + `${id}/latest/refresh-results`;
    return this.httpClient.post<void>(url, {});
  }

  deleteMultiple(ids: number[]): Observable<{ failed: number[] }> {
    const url = this.docsEndpoint + `by-ids`;
    return this.httpClient.post<{ failed: number[] }>(url, {ids});
  }

  userChecked(id: number): Observable<void> {
    const url = this.endpointV1 + `${id}/latest/user-checked`;
    return this.httpClient.post<void>(url, {});
  }

  preview(id: number) {
    const url = this.downloadPreviewPdfUrl(id);
    return this.httpClient.get(url, {responseType: 'text'});
  }


// /app/id/audit
  findByApplicationId(applicationId: number, category?: string): Observable<ListAppAssessmentForAppRefResponse> {
    const url = this.endpointV2 + `apps/${applicationId}/categories/${category}/`;
    return this.httpClient.get<ListAppAssessmentForAppRefResponse>(url);
  }

  // findByApplicationId(applicationId: number, category?: string): Observable<ListAppAssessmentForAppRefResponse> {
  //   let url = this.endpointV2 + `?sort=appAssessmentDocId,DESC&q=applicationId::${applicationId}`;
  //   if (category) {
  //     url += `&q=category::${category}`;
  //   }
  //   return this.httpClient.get<ListAppAssessmentForAppRefResponse>(url);
  // }

  createSimplified(body: AppAssessmentSimplifiedCreateRequest): Observable<CreateDocResponse> {
    const url = this.docsEndpoint + `simplified`;
    return this.httpClient.post<CreateDocResponse>(url, body);
  }

  createPrimatoo(body: AppAssessmentSimplifiedCreateRequest): Observable<CreateDocResponse> {
    const url = this.docsEndpoint;
    return this.httpClient.post<CreateDocResponse>(url, body);
  }

  updateSimplified(id: number, body: AppAssessmentSimplifiedUpdateRequest): Observable<void> {
    const url = this.endpointV1 + `${id}/latest/simplified`;
    return this.httpClient.put<void>(url, body);
  }

  getDocUserPermission(docId: number): Observable<GetAppAsAvailableOperationsResponse> {
    const url = this.endpointV2 + `${docId}/latest/user-operations/`;
    return this.httpClient.get<GetAppAsAvailableOperationsResponse>(url);
  }

  public availableApp(): Observable<ListAppForNewAssessmentResponse> {
    const url = this.endpointV2 + `available-apps/`;
    return this.httpClient.get<ListAppForNewAssessmentResponse>(url);
  }
}
